import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { isCustomHost, storePath } from "../lib/storeUrl";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Search, Heart, Phone, MapPin, Instagram, Facebook, Truck,
  ShieldCheck, RotateCcw, BadgePercent, Minus, Plus, Trash2, Send, ArrowRight, Menu, X,
} from "lucide-react";
import { toast } from "sonner";
import { UserRound } from "lucide-react";
import { getCustomerInfo } from "../lib/customer";
import api, { fmtINR, errMsg, fileUrl } from "../lib/api";
import { setFavicon } from "../lib/utils";
import { getCart, saveCart, getWish, saveWish, addItem, cartCount, cartSubtotal, discountPct } from "../lib/cart";
import { VARIANTS, themeVars, headingFont, SafeImg, SectionHead, ProductCard, StoreHero } from "../components/StoreKit";
import RESite from "./realestate/RESite";
import SiteRenderer from "../components/SiteRenderer";
import { AdSlot, AdNetworkScripts } from "../ads/AdEngine";
import ImportedTheme from "../components/ImportedTheme";
import { applySeo } from "../lib/seo";
import { initPixel, initGa4, track, captureUtm } from "../lib/tracking";
import EditorCanvas from "../editor/EditorCanvas";
import { Reveal, Marquee } from "../components/Shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Input } from "../components/ui/input";

export default function Storefront({ slugOverride = null, shopMode = false }) {
  const { slug: routeSlug } = useParams();
  const slug = slugOverride || routeSlug;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const a = searchParams.get("aff");
    if (a) sessionStorage.setItem(`mkt_aff_${slug}`, a);
  }, [searchParams, slug]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [catState, setCatState] = useState("");
  const cat = shopMode ? (searchParams.get("category") || "") : catState;
  const setCat = (c) => { if (shopMode) setSearchParams(c ? { category: c } : {}); else setCatState(c); };
  const goShop = (c = "") => navigate(storePath(slug, c ? `/shop?category=${encodeURIComponent(c)}` : "/shop"));
  const [cart, setCart] = useState(() => getCart(slug));
  const [wish, setWish] = useState(() => getWish(slug));
  const [cartOpen, setCartOpen] = useState(false);
  const [wishOpen, setWishOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [track, setTrack] = useState("");
  const [nlEmail, setNlEmail] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [legal, setLegal] = useState(null);

  useEffect(() => {
    api.get(`/public/store/${slug}`).then((r) => { setData(r.data); const fav = r.data.store?.settings?.favicon_url || r.data.branding?.favicon_url; if (fav) setFavicon(fav); }).catch((e) => setError(errMsg(e)));
    captureUtm();
    api.get(`/public/store/${slug}/seo`, { headers: { "X-Miltroo-Host": window.location.hostname } })
      .then((r) => { applySeo(r.data); if (r.data.pixel_id) initPixel(r.data.pixel_id); if (r.data.ga4_id) initGa4(r.data.ga4_id); track("page_view", {}, { scope: "store", slug }); })
      .catch(() => {});
  }, [slug]);
  useEffect(() => {
    api.get(`/public/store/${slug}/products`, { params: { q, category: cat } }).then((r) => setProducts(r.data)).catch(() => {});
  }, [slug, q, cat]);
  useEffect(() => saveCart(slug, cart), [cart, slug]);
  useEffect(() => saveWish(slug, wish), [wish, slug]);
  useEffect(() => {
    const b = data?.branding || {};
    const t = b.website_title || data?.store?.name;
    if (t) document.title = t;
    if (b.favicon_url) {
      let l = document.querySelector("link[rel='icon']");
      if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); }
      l.href = fileUrl(b.favicon_url);
    }
  }, [data]);
  useEffect(() => { if (shopMode) window.scrollTo({ top: 0 }); }, [shopMode, cat]);
  useEffect(() => {
    const origin = window.location.origin;
    const canonical = isCustomHost() ? `${origin}/` : `${origin}/site/${slug}`;
    let c = document.querySelector("link[rel='canonical']");
    if (!c) { c = document.createElement("link"); c.rel = "canonical"; document.head.appendChild(c); }
    c.href = canonical;
    let og = document.querySelector("meta[property='og:url']");
    if (!og) { og = document.createElement("meta"); og.setAttribute("property", "og:url"); document.head.appendChild(og); }
    og.setAttribute("content", canonical);
  }, [slug]);

  const theme = data?.theme;
  const store = data?.store;
  const colors = store?.settings?.colors || theme?.colors || {};
  const vars = useMemo(() => themeVars(colors, theme), [colors, theme]);
  const HS = theme?.header_style || (VARIANTS[theme?.variant]?.header === "solid" ? "solid" : VARIANTS[theme?.variant]?.header === "dark" ? "dark" : "glass");
  const headerStyle =
    HS === "solid" ? { backgroundColor: "var(--sp)", color: "#fff" }
    : HS === "dark" ? { backgroundColor: "#0B1220", color: "#fff" }
    : HS === "gradient" ? { backgroundImage: "linear-gradient(90deg, var(--sp), var(--sa))", color: "#fff" }
    : HS === "minimal" ? { backgroundColor: "var(--sbg)", color: "var(--sp)", borderBottom: "1px solid rgba(0,0,0,0.08)" }
    : HS === "centered" ? { backgroundColor: "var(--sbg)", color: "var(--sp)", borderBottom: "2px solid var(--sp)" }
    : { backgroundColor: "rgba(255,255,255,0.85)", color: "var(--sp)", backdropFilter: "blur(16px)" };
  const headerTextIsLight = ["solid", "dark", "gradient"].includes(HS);
  const variant = VARIANTS[theme?.variant] || VARIANTS.market;
  const radius = theme?.radius || "10px";
  const head = headingFont(theme);

  const addToCart = (p, qty = 1, variation = "") => {
    setCart((c) => addItem(c, p, qty, variation));
    track("add_to_cart", { value: p.price, currency: "INR", items: [{ id: p.id, qty }] }, { scope: "store", slug });
    toast.success(`${p.name} added to cart`, { duration: 4000, closeButton: true, action: { label: "View Cart", onClick: () => navigate(storePath(slug, "/cart")) } });
  };
  const buyNow = (p) => { setCart((c) => addItem(c, p, 1, "")); navigate(storePath(slug, "/checkout")); };
  const toggleWish = (p) => {
    const has = wish.some((w) => w.id === p.id);
    setWish((w) => has ? w.filter((x) => x.id !== p.id) : [...w, { id: p.id, name: p.name, price: p.price, image: p.images?.[0] || "" }]);
    toast.success(has ? "Removed from wishlist" : `${p.name} saved to wishlist`);
  };
  const setQty = (key, d) => setCart((prev) => prev.map((c) => (c.key === key ? { ...c, qty: Math.max(1, c.qty + d) } : c)));
  const removeItem = (key) => setCart((prev) => prev.filter((c) => c.key !== key));

  const subscribe = async (e) => {
    e.preventDefault();
    setNlBusy(true);
    try { await api.post(`/public/store/${slug}/subscribe`, { email: nlEmail }); toast.success("Subscribed! Watch your inbox for offers."); setNlEmail(""); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setNlBusy(false); }
  };

  const trackOrder = async () => {
    if (!track.trim()) return;
    try {
      const { data: o } = await api.get(`/public/orders/${track}`);
      toast.success(`${o.order_number}: ${o.status.toUpperCase()} · payment ${o.payment_status} · ${fmtINR(o.total)}`, { duration: 6000 });
    } catch { toast.error("Order not found"); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-background"><p data-testid="storefront-error" className="text-muted-foreground">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;
  if (data.is_re) return <RESite slugOverride={slug} baseOv={slugOverride ? "" : `/site/${slug}`} />;

  // Real Estate themes render the dedicated RE storefront.
  if (data?.theme?.re) return <Navigate to={slugOverride ? "/re" : `/site/${slug}/re`} replace />;

  // Imported themes: converted original design + MILTROO data injected server-side,
  // commerce wired to native MILTROO routes by the mz-runtime.
  if (data.theme?.imported) return <ImportedTheme slug={slug} engine={data.theme?.engine || "static"} />;

  const info = store.business_info || {};
  const branding = data.branding || {};
  const legalPages = store.settings?.legal || {};
  const LEGAL_LINKS = [
    ["privacy_policy", "Privacy Policy"], ["terms", "Terms & Conditions"], ["refund_policy", "Refund Policy"],
    ["shipping_policy", "Shipping Policy"], ["contact_page", "Contact Us"],
  ].filter(([k]) => (legalPages[k] || "").trim());
  const published = data.content;
  const pubSections = (published?.sections || []).filter((s) => !s.hidden);
  const hasContent = pubSections.some((s) => !["header", "footer"].includes(s.type));
  const headerSec = pubSections.find((s) => s.type === "header");
  const footerSec = pubSections.find((s) => s.type === "footer");
  const isShop = store.type === "ecommerce" || products.length > 0;
  const featured = products.slice(0, 4);
  const deals = products.filter((p) => discountPct(p) > 0);
  const latest = products.slice(0, 8);
  const count = cartCount(cart);
  const custInfo = getCustomerInfo(slug);
  const subtotal = cartSubtotal(cart);

  return (
    <div style={{ ...vars, scrollBehavior: "smooth", fontFamily: "var(--sbfont)" }} className="min-h-screen bg-[var(--sbg)] text-[var(--st)]" data-testid="storefront" id="top">
      {/* Promo bar */}
      <div className="text-center text-[11px] font-bold tracking-wide py-2 px-4 text-white"
        style={{ backgroundImage: "linear-gradient(90deg, var(--sp), var(--sa))" }} data-testid="promo-bar">
        {isShop ? <>Free shipping on orders over ₹999 · Use code <span className="underline underline-offset-2">SAVE10</span> at checkout</> : `${store.name} · ${info.category || theme?.category || "Premium"} · ${info.phone || "Welcome"}`}
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 shadow-sm" style={headerStyle} data-testid="store-header">
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3 ${HS === "centered" ? "justify-center" : ""}`}>
          <button className="md:hidden h-9 w-9 rounded-full flex items-center justify-center" onClick={() => setMobileNav(!mobileNav)} data-testid="store-mobile-menu">
            {mobileNav ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          <Link to={storePath(slug, "")} className="flex items-center gap-2.5 min-w-0">
            {(headerSec?.props?.logo || info.logo_url || branding.logo_url) ? (
              <img src={fileUrl(headerSec?.props?.logo || info.logo_url || branding.logo_url)} alt="" className="h-9 object-contain shrink-0" data-testid="storefront-logo" />
            ) : (
            <div className="h-9 w-9 rounded-lg flex items-center justify-center font-display font-extrabold text-sm shrink-0"
              style={{ backgroundColor: "var(--sa)", color: headerTextIsLight ? "var(--sp)" : "#fff" }}>
              {store.name[0]}
            </div>
            )}
            <span data-testid="storefront-name" className={`${head} text-lg tracking-tight truncate`}>{headerSec?.props?.brandName || store.name}</span>
          </Link>
          {isShop && (
          <div className="hidden md:flex flex-1 max-w-md mx-4 relative">
            <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 opacity-50" />
            <input data-testid="storefront-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
              className={`w-full h-10 pl-10 pr-4 rounded-full text-sm outline-none ${headerTextIsLight ? "bg-white/15 placeholder-white/60 text-white" : "bg-black/5 placeholder-zinc-400 text-zinc-900"}`} />
          </div>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            {isShop && (
            <Link to={storePath(slug, "/account")} data-testid="account-button"
              className={`h-10 px-3 rounded-full flex items-center gap-1.5 text-sm font-bold transition-colors ${headerTextIsLight ? "hover:bg-white/15" : "hover:bg-black/5"}`}>
              <UserRound className="h-4 w-4" /> <span className="hidden md:inline">{custInfo?.name ? custInfo.name.split(" ")[0] : "Login"}</span>
            </Link>
            )}
            {info.phone && (
              <a data-testid="storefront-call" href={`tel:${info.phone}`} className={`hidden sm:flex h-10 w-10 rounded-full items-center justify-center transition-colors ${headerTextIsLight ? "hover:bg-white/15" : "hover:bg-black/5"}`}>
                <Phone className="h-4 w-4" />
              </a>
            )}
            {isShop && (
            <button data-testid="wishlist-button" onClick={() => setWishOpen(true)} className={`relative h-10 w-10 rounded-full flex items-center justify-center transition-colors ${headerTextIsLight ? "hover:bg-white/15" : "hover:bg-black/5"}`}>
              <Heart className="h-4 w-4" />
              {wish.length > 0 && <span data-testid="wishlist-count" className="absolute -top-0.5 -right-0.5 h-4.5 min-w-4.5 px-1 h-4 rounded-full text-[9px] font-extrabold flex items-center justify-center text-white" style={{ backgroundColor: "var(--sa)" }}>{wish.length}</span>}
            </button>
            )}
            {isShop && (
            <button data-testid="cart-button" onClick={() => setCartOpen(true)}
              className="relative h-10 px-4 rounded-full flex items-center gap-2 font-bold text-sm transition-transform active:scale-95"
              style={{ backgroundColor: "var(--sa)", color: "#fff" }}>
              <ShoppingBag className="h-4 w-4" /> <span className="hidden sm:inline">Cart</span>
              {count > 0 && <span data-testid="cart-count" className="h-5 min-w-5 px-1 rounded-full bg-white text-[10px] font-extrabold flex items-center justify-center" style={{ color: "var(--sp)" }}>{count}</span>}
            </button>
            )}
          </div>
        </div>
        {/* Category nav — desktop/tablet only; mobile uses the ☰ panel below */}
        {data.categories.length > 0 && (
          <nav className={`border-t hidden md:block ${headerTextIsLight ? "border-white/10" : "border-black/5"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto py-2">
              <button data-testid="cat-all" onClick={() => goShop("")}
                className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${!cat ? "text-white" : "opacity-70 hover:opacity-100"}`}
                style={!cat ? { backgroundColor: "var(--sa)" } : {}}>All</button>
              {data.categories.map((c) => (
                <button key={c} data-testid={`cat-${c.toLowerCase()}`} onClick={() => goShop(c)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${cat === c ? "text-white" : "opacity-70 hover:opacity-100"}`}
                  style={cat === c ? { backgroundColor: "var(--sa)" } : {}}>{c}</button>
              ))}
            </div>
          </nav>
        )}

        {/* Mobile menu panel — vertical, theme-matched, touch-friendly */}
        <AnimatePresence>
          {mobileNav && (
            <motion.nav initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className={`md:hidden overflow-hidden border-t ${headerTextIsLight ? "border-white/10" : "border-black/5"}`}
              data-testid="store-mobile-panel">
              <div className="px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">
                {data.categories.length > 0 && (
                  <>
                    <p className="px-3 pb-1 text-[10px] font-extrabold uppercase tracking-widest opacity-50">Shop</p>
                    <button data-testid="mobile-cat-all" onClick={() => { goShop(""); setMobileNav(false); }}
                      className={`w-full text-left px-4 py-3 rounded-xl text-sm font-bold transition-colors ${!cat ? "text-white" : ""}`}
                      style={!cat ? { backgroundColor: "var(--sp)" } : {}}>All Products</button>
                    {data.categories.map((c) => (
                      <button key={c} data-testid={`mobile-cat-${c.toLowerCase()}`} onClick={() => { goShop(c); setMobileNav(false); }}
                        className={`w-full text-left px-4 py-3 rounded-xl text-sm font-bold transition-colors ${cat === c ? "text-white" : "hover:bg-black/5"}`}
                        style={cat === c ? { backgroundColor: "var(--sp)" } : {}}>{c}</button>
                    ))}
                  </>
                )}
                {isShop && (
                  <>
                    <p className="px-3 pt-3 pb-1 text-[10px] font-extrabold uppercase tracking-widest opacity-50">Account</p>
                    <Link to={storePath(slug, "/account")} onClick={() => setMobileNav(false)} data-testid="mobile-account-link"
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold hover:bg-black/5 transition-colors">
                      <UserRound className="h-4 w-4" /> {custInfo?.name ? `My Account (${custInfo.name.split(" ")[0]})` : "Login / My Account"}
                    </Link>
                    <button onClick={() => { setWishOpen(true); setMobileNav(false); }} data-testid="mobile-wishlist-link"
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold hover:bg-black/5 transition-colors">
                      <Heart className="h-4 w-4" /> Wishlist{wish.length > 0 ? ` (${wish.length})` : ""}
                    </button>
                    <button onClick={() => { setCartOpen(true); setMobileNav(false); }} data-testid="mobile-cart-link"
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold hover:bg-black/5 transition-colors">
                      <ShoppingBag className="h-4 w-4" /> Cart{count > 0 ? ` (${count})` : ""}
                    </button>
                  </>
                )}
                {LEGAL_LINKS.length > 0 && (
                  <>
                    <p className="px-3 pt-3 pb-1 text-[10px] font-extrabold uppercase tracking-widest opacity-50">Help</p>
                    {LEGAL_LINKS.map(([k, label]) => (
                      <button key={k} data-testid={`mobile-legal-${k}`} onClick={() => { setLegal([k, label]); setMobileNav(false); }}
                        className="w-full text-left px-4 py-3 rounded-xl text-sm font-bold hover:bg-black/5 transition-colors">{label}</button>
                    ))}
                  </>
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile search */}
      {isShop && (
      <div className="md:hidden px-4 pt-3 relative">
        <Search className="h-4 w-4 absolute left-7 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
          className="w-full h-10 pl-10 pr-4 rounded-full text-sm bg-black/5 outline-none text-zinc-900" />
      </div>
      )}

      <AdSlot slug={slug} page={shopMode ? "shop" : "home"} slot="top" />

      {shopMode && (
        <main data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 min-h-[55vh]">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="uppercase text-[11px] tracking-[0.25em] font-extrabold" style={{ color: "var(--sa)" }}>{store.name} · Shop</p>
            <h1 className={`${head} text-4xl sm:text-5xl tracking-tighter mt-2`} style={{ color: "var(--sp)" }} data-testid="shop-title">
              {cat || "All Products"}
            </h1>
            <p className="text-sm mt-2 font-semibold" style={{ color: "var(--st)", opacity: 0.55 }} data-testid="shop-product-count">
              {products.length} product{products.length === 1 ? "" : "s"}{cat ? ` in ${cat}` : ""}{q ? ` matching "${q}"` : ""}
            </p>
          </motion.div>
          {data.categories.length > 0 && (
            <div className="flex gap-2 mt-7 overflow-x-auto pb-2 sm:flex-wrap" data-testid="shop-categories">
              <button data-testid="shop-cat-all" onClick={() => goShop("")}
                className={`px-5 py-2.5 rounded-full text-xs font-extrabold whitespace-nowrap transition-all active:scale-95 ${!cat ? "text-white shadow-md" : "hover:opacity-80"}`}
                style={!cat ? { backgroundColor: "var(--sp)" } : { backgroundColor: "var(--ss)", color: "var(--sp)" }}>
                All Categories
              </button>
              {data.categories.map((c) => (
                <button key={c} data-testid={`shop-cat-${c.toLowerCase()}`} onClick={() => goShop(c)}
                  className={`px-5 py-2.5 rounded-full text-xs font-extrabold whitespace-nowrap transition-all active:scale-95 ${cat === c ? "text-white shadow-md" : "hover:opacity-80"}`}
                  style={cat === c ? { backgroundColor: "var(--sp)" } : { backgroundColor: "var(--ss)", color: "var(--sp)" }}>
                  {c}
                </button>
              ))}
            </div>
          )}
          {products.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8" data-testid="shop-products-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} p={p} delay={Math.min(i, 8) * 0.04} radius={radius}
                  onOpen={() => navigate(storePath(slug, `/product/${p.id}`))}
                  onAdd={() => addToCart(p)} onBuy={() => buyNow(p)}
                  wished={wish.some((w) => w.id === p.id)} onWish={() => toggleWish(p)} />
              ))}
            </div>
          ) : (
            <div className="text-center py-24" data-testid="shop-empty">
              <ShoppingBag className="h-10 w-10 mx-auto" style={{ color: "var(--st)", opacity: 0.25 }} />
              <p className="mt-4 text-sm font-bold" style={{ color: "var(--st)", opacity: 0.6 }}>
                No products found{cat ? ` in ${cat}` : ""}{q ? ` for "${q}"` : ""}.
              </p>
              {(cat || q) && (
                <button data-testid="shop-reset" onClick={() => { setQ(""); goShop(""); }}
                  className="mt-6 px-7 py-3 rounded-full text-xs font-extrabold text-white active:scale-95 transition-transform"
                  style={{ backgroundColor: "var(--sp)" }}>
                  View All Products
                </button>
              )}
            </div>
          )}
        </main>
      )}
      {!shopMode && hasContent && (
        <EditorCanvas doc={published} theme={theme} store={store} info={info} branding={branding} products={products} live slug={slug}
          handlers={{ onAdd: (p) => addToCart(p), onBuy: buyNow, onOpen: (p) => navigate(storePath(slug, `/product/${p.id}`)), onCart: () => navigate(storePath(slug, "/cart")), onShopNow: () => goShop(""), onSecondary: () => goShop(""), onCategory: (c) => goShop(c), onSearch: (v) => setQ(v) }} />
      )}
      {!shopMode && hasContent && <AdSlot slug={slug} page="home" slot="after_hero" />}
      {!shopMode && !hasContent && (<>
      {/* ===== HERO (theme-specific, falls back to variant) ===== */}
      {theme?.hero ? (
        <StoreHero theme={theme} store={store} info={info} products={products} head={head} radius={radius}
          onShopNow={isShop ? () => goShop("") : undefined} />
      ) : (<>
      {variant.hero === "split" && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest"
              style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>
              <BadgePercent className="h-3.5 w-3.5" /> {info.category || theme?.category}
            </span>
            <h1 className={`${head} text-5xl sm:text-6xl tracking-tighter leading-[1.02] mt-5`} style={{ color: "var(--sp)" }}>
              {store.name}
            </h1>
            <p className="mt-4 text-base sm:text-lg max-w-md leading-relaxed" style={{ color: "var(--st)", opacity: 0.7 }}>{info.description}</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <button data-testid="hero-shop-now" onClick={() => goShop("")}
                className="px-8 py-3.5 rounded-full font-bold text-sm text-white transition-transform active:scale-95 shadow-lg hover:opacity-90"
                style={{ backgroundColor: "var(--sp)" }}>
                Shop Now <ArrowRight className="inline h-4 w-4 ml-1" />
              </button>
              <button onClick={() => document.getElementById("deals")?.scrollIntoView({ behavior: "smooth" })}
                className="px-8 py-3.5 rounded-full font-bold text-sm border-2 transition-all active:scale-95 hover:bg-black/5"
                style={{ borderColor: "var(--sp)", color: "var(--sp)" }}>
                Today's Deals
              </button>
            </div>
            <div className="flex gap-6 mt-8">
              {[["Free Shipping", Truck], ["Secure Payments", ShieldCheck], ["Easy Returns", RotateCcw]].map(([t, Icon]) => (
                <span key={t} className="flex items-center gap-2 text-xs font-bold" style={{ color: "var(--st)", opacity: 0.65 }}>
                  <Icon className="h-4 w-4" style={{ color: "var(--sa)" }} />{t}
                </span>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
            <div className="absolute -inset-4 rounded-[2rem] opacity-25 blur-2xl" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }} />
            <SafeImg src={theme?.hero || theme?.preview} alt={store.name}
              className="relative w-full aspect-[4/3] object-cover shadow-2xl" style={{ borderRadius: `calc(${radius} * 2)` }} />
            <div className="absolute -bottom-5 -left-4 bg-white rounded-xl shadow-xl px-5 py-3.5 flex items-center gap-3 animate-float">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "var(--sa)" }} />
              <span className="text-xs font-extrabold" style={{ color: "var(--sp)" }}>{products.length}+ products in store</span>
            </div>
          </motion.div>
        </section>
      )}

      {variant.hero === "fullbleed" && (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <SafeImg src={theme?.hero || theme?.preview} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(100deg, ${colors.primary}E8 25%, ${colors.primary}66 60%, transparent)` }} />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-28 sm:py-40">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <p className="uppercase text-[11px] tracking-[0.3em] font-extrabold" style={{ color: "var(--sa)" }}>{info.category || "New Collection"}</p>
              <h1 className={`${head} text-5xl sm:text-7xl tracking-tighter text-white mt-4 max-w-2xl leading-[1.0]`}>{store.name}</h1>
              <p className="mt-5 text-white/80 max-w-md text-base sm:text-lg leading-relaxed">{info.description}</p>
              <button data-testid="hero-shop-now" onClick={() => goShop("")}
                className="mt-8 px-9 py-4 rounded-full font-bold text-sm text-white transition-transform active:scale-95 shadow-xl hover:scale-105 duration-300"
                style={{ backgroundColor: "var(--sa)" }}>
                Explore the Collection <ArrowRight className="inline h-4 w-4 ml-1" />
              </button>
            </motion.div>
          </div>
        </section>
      )}

      {variant.hero === "center" && (
        <section className="relative overflow-hidden py-16 sm:py-24" style={{ backgroundColor: "var(--ss)" }}>
          <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full opacity-30 blur-3xl animate-blob" style={{ background: "var(--sa)" }} />
          <div className="absolute -bottom-24 -right-16 h-80 w-80 rounded-full opacity-25 blur-3xl animate-blob" style={{ background: "var(--sp)", animationDelay: "-6s" }} />
          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-block px-4 py-1.5 rounded-full bg-white text-[11px] font-extrabold uppercase tracking-widest shadow-sm" style={{ color: "var(--sp)" }}>
                {info.category || theme?.category}
              </span>
              <h1 className={`${head} text-5xl sm:text-6xl tracking-tighter mt-5`} style={{ color: "var(--sp)" }}>{store.name}</h1>
              <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "var(--st)", opacity: 0.7 }}>{info.description}</p>
              <button data-testid="hero-shop-now" onClick={() => goShop("")}
                className="mt-8 px-9 py-4 rounded-full font-bold text-sm text-white transition-transform active:scale-95 shadow-lg hover:scale-105 duration-300"
                style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                Shop the Collection <ArrowRight className="inline h-4 w-4 ml-1" />
              </button>
            </motion.div>
          </div>
        </section>
      )}
      </>)}

      <Marquee items={isShop ? ["FREE SHIPPING OVER ₹999", "NEW ARRIVALS WEEKLY", "EASY RETURNS", "SECURE PAYMENTS", info.category?.toUpperCase() || "PREMIUM QUALITY"] : [(theme?.category || "PREMIUM").toUpperCase(), "TRUSTED & VERIFIED", "PREMIUM QUALITY", "TOP RATED"]} />

      <AdSlot slug={slug} page={shopMode ? "shop" : "home"} slot="after_hero" />

      {/* Non-ecommerce site content (about, services, stats, team, testimonials, FAQ, CTA, contact) */}
      {theme?.site && <SiteRenderer site={theme.site} head={head} radius={radius} info={info} theme={theme} store={store} brand={branding.brand_name || ""} defaults={!hasContent && data.content_defaults?.services ? data.content_defaults : null} live slug={slug} />}

      {/* ===== FEATURED ===== */}
      {isShop && featured.length > 0 && (
        <section id="products" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <Reveal><SectionHead overline="Handpicked for you" title="Featured Products" /></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-10">
            {featured.map((p, i) => (
              <ProductCard key={p.id} p={p} delay={i * 0.06} radius={radius}
                onOpen={() => navigate(storePath(slug, `/product/${p.id}`))}
                onAdd={() => addToCart(p)} onBuy={() => buyNow(p)}
                wished={wish.some((w) => w.id === p.id)} onWish={() => toggleWish(p)} />
            ))}
          </div>
        </section>
      )}

      {/* ===== DEALS ===== */}
      {deals.length > 0 && (
        <section id="deals" className="py-16" style={{ backgroundColor: "var(--ss)" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <Reveal><SectionHead overline="Limited time" title="Deals of the Day" /></Reveal>
            <div className="flex gap-5 overflow-x-auto pb-4 mt-10 snap-x">
              {deals.map((p) => (
                <motion.div key={p.id} whileHover={{ y: -6 }} data-testid={`deal-${p.id}`}
                  className="snap-start shrink-0 w-64 bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => navigate(storePath(slug, `/product/${p.id}`))}>
                  <div className="relative h-44 overflow-hidden">
                    <SafeImg src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-white" style={{ backgroundColor: "var(--sa)" }}>
                      {discountPct(p)}% OFF
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="font-bold text-sm truncate" style={{ color: "var(--st)" }}>{p.name}</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-display font-extrabold" style={{ color: "var(--sp)" }}>{fmtINR(p.price)}</span>
                      <span className="text-xs line-through text-zinc-400">{fmtINR(p.compare_price)}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== LATEST ===== */}
      {isShop && latest.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <Reveal><SectionHead overline="Just landed" title="Latest Products" /></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-10">
            {latest.map((p, i) => (
              <ProductCard key={p.id} p={p} delay={i * 0.04} radius={radius}
                onOpen={() => navigate(storePath(slug, `/product/${p.id}`))}
                onAdd={() => addToCart(p)} onBuy={() => buyNow(p)}
                wished={wish.some((w) => w.id === p.id)} onWish={() => toggleWish(p)} />
            ))}
          </div>
        </section>
      )}

      {isShop && products.length === 0 && (
        <p className="text-center py-20 text-sm" style={{ color: "var(--st)", opacity: 0.5 }}>No products found{q ? ` for "${q}"` : ""}.</p>
      )}

      {/* ===== DEFAULT SERVICES + PRICING (ecommerce themes without editor content) ===== */}
      {!theme?.site && data.content_defaults?.services && (
        <div data-testid="default-service-pricing">
          <EditorCanvas doc={{ sections: [data.content_defaults.services, data.content_defaults.pricing].filter(Boolean) }}
            theme={theme} store={store} info={info} branding={branding} live slug={slug} />
        </div>
      )}

      {/* ===== TESTIMONIALS (ecommerce default — site themes render their own) ===== */}
      {!theme?.site && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <Reveal><SectionHead center overline="Loved by customers" title="What People Say" /></Reveal>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {[
            ["Ananya R.", "Beautiful quality and super fast delivery. Ordering was effortless."],
            ["Vikram M.", "The attention to detail is unreal. This is my go-to store now."],
            ["Priya N.", "Smooth checkout, great packaging, and the products exceeded expectations."],
          ].map(([name, quote], i) => (
            <Reveal key={name} delay={i * 0.1}>
              <div className="rounded-xl border border-black/5 bg-white p-6 h-full hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="flex gap-0.5 mb-3">{[1, 2, 3, 4, 5].map((s) => <span key={s} className="text-sm" style={{ color: "var(--sa)" }}>★</span>)}</div>
                <p className="text-sm leading-relaxed" style={{ color: "var(--st)", opacity: 0.8 }}>"{quote}"</p>
                <p className="text-xs font-extrabold mt-4" style={{ color: "var(--sp)" }}>{name} · Verified Buyer</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* ===== NEWSLETTER (ecommerce only — site themes render their own CTA + footer) ===== */}
      {!theme?.site && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <Reveal>
          <div className="rounded-2xl p-10 sm:p-14 text-center text-white relative overflow-hidden"
            style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }} data-testid="newsletter-section">
            <div className="absolute -top-14 -right-14 h-48 w-48 rounded-full bg-white/10 animate-float" />
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter relative">Get offers before anyone else</h2>
            <p className="text-white/80 mt-3 relative text-sm">Join the {store.name} newsletter for early drops and exclusive deals.</p>
            <form onSubmit={subscribe} className="relative max-w-md mx-auto mt-7 flex gap-2">
              <Input data-testid="newsletter-email" type="email" required value={nlEmail} onChange={(e) => setNlEmail(e.target.value)}
                placeholder="you@email.com" className="h-12 rounded-full bg-white/95 text-zinc-900 border-0" />
              <button data-testid="newsletter-subscribe" disabled={nlBusy}
                className="h-12 px-6 rounded-full bg-zinc-900 text-white font-bold text-sm flex items-center gap-2 active:scale-95 transition-transform shrink-0">
                <Send className="h-4 w-4" /> {nlBusy ? "…" : "Subscribe"}
              </button>
            </form>
          </div>
        </Reveal>
      </section>
      )}
      </>)}

      <AdSlot slug={slug} page={shopMode ? "shop" : "home"} slot="footer" />
      <AdNetworkScripts slug={slug} page={shopMode ? "shop" : "home"} />

      {/* ===== FOOTER (ecommerce; site themes render an industry footer via SiteRenderer) ===== */}
      {(shopMode || !theme?.site || hasContent) && (
      <footer style={{ backgroundColor: "var(--sp)" }} className="text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <p className={`${head} text-xl`}>{store.name}</p>
            <p className="text-sm text-white/60 mt-3 leading-relaxed">{footerSec?.props?.description || info.description}</p>
            <div className="flex gap-2 mt-4">
              {info.socials?.instagram && <a data-testid="social-instagram" href={`https://instagram.com/${info.socials.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><Instagram className="h-4 w-4" /></a>}
              {info.socials?.facebook && <a data-testid="social-facebook" href={`https://facebook.com/${info.socials.facebook}`} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><Facebook className="h-4 w-4" /></a>}
            </div>
          </div>
          {isShop && (
          <div>
            <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Shop</p>
            <div className="space-y-2.5">
              <button data-testid="footer-shop-all" onClick={() => goShop("")} className="block text-sm text-white/75 hover:text-white transition-colors">All Products</button>
              {data.categories.slice(0, 4).map((c) => (
                <button key={c} data-testid={`footer-shop-${c.toLowerCase()}`} onClick={() => goShop(c)} className="block text-sm text-white/75 hover:text-white transition-colors">{c}</button>
              ))}
            </div>
          </div>
          )}
          <div>
            <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Contact</p>
            <div className="space-y-2.5 text-sm text-white/75">
              {info.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{info.phone}</p>}
              {info.email && <p>{info.email}</p>}
              {info.address && <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />{info.address}</p>}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Track your order</p>
            <div className="flex gap-2">
              <Input data-testid="track-order-input" value={track} onChange={(e) => setTrack(e.target.value)} placeholder="ORD-12345"
                className="h-10 rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/40" />
              <button data-testid="track-order-button" onClick={trackOrder}
                className="h-10 px-5 rounded-full font-bold text-sm shrink-0 active:scale-95 transition-transform" style={{ backgroundColor: "var(--sa)" }}>Track</button>
            </div>
          </div>
        </div>
        {LEGAL_LINKS.length > 0 && (
          <div className="border-t border-white/10 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap justify-center gap-x-6 gap-y-2" data-testid="legal-links">
              {LEGAL_LINKS.map(([k, label]) => (
                <button key={k} data-testid={`legal-link-${k}`} onClick={() => setLegal([k, label])}
                  className="text-xs text-white/60 hover:text-white transition-colors font-semibold">{label}</button>
              ))}
            </div>
          </div>
        )}
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">
          © {new Date().getFullYear()} {store.name} · {footerSec?.props?.copyright || branding.footer_text || (branding.brand_name ? `Powered by ${branding.brand_name}` : "Powered by MILTROO")}
        </div>
      </footer>
      )}

      {/* ===== LEGAL PAGE OVERLAY (theme-styled) ===== */}
      {legal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backgroundColor: "var(--sbg)", color: "var(--st)", fontFamily: "var(--sbfont)" }} data-testid="legal-page-overlay">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
            <button data-testid="legal-close" onClick={() => setLegal(null)}
              className="h-10 w-10 rounded-full border-2 flex items-center justify-center mb-8 hover:opacity-70 transition-opacity"
              style={{ borderColor: "var(--sp)", color: "var(--sp)" }}><X className="h-5 w-5" /></button>
            <h1 className={`${head} text-3xl sm:text-4xl tracking-tight`} style={{ color: "var(--sp)" }}>{legal[1]}</h1>
            <p className="text-xs mt-2" style={{ color: "var(--st)", opacity: 0.5 }}>{store.name}</p>
            <div className="text-sm leading-relaxed mt-8 whitespace-pre-wrap" style={{ color: "var(--st)", opacity: 0.8 }} data-testid="legal-content">{legalPages[legal[0]]}</div>
          </div>
        </div>
      )}

      {/* ===== CART DRAWER ===== */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent className="w-full sm:max-w-md flex flex-col p-6" style={vars}>
          <SheetHeader><SheetTitle className="font-display">Your Cart ({count})</SheetTitle></SheetHeader>
          <div className="flex-1 overflow-y-auto mt-4 space-y-4 pr-1">
            {cart.length === 0 && <p data-testid="cart-empty" className="text-sm text-muted-foreground text-center py-12">Your cart is empty</p>}
            {cart.map((c) => (
              <div key={c.key} data-testid={`cart-item-${c.key}`} className="flex gap-3 items-center">
                <div className="h-16 w-16 rounded-md bg-muted overflow-hidden shrink-0">{c.image && <img src={c.image} alt="" className="h-full w-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{c.name}</p>
                  {c.variation && <p className="text-[10px] text-muted-foreground">{c.variation}</p>}
                  <p className="text-xs text-muted-foreground">{fmtINR(c.price)}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`drawer-qty-minus-${c.key}`} onClick={() => setQty(c.key, -1)} className="h-7 w-7 rounded-full border border-border flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                  <span className="w-6 text-center text-sm font-bold">{c.qty}</span>
                  <button data-testid={`drawer-qty-plus-${c.key}`} onClick={() => setQty(c.key, 1)} className="h-7 w-7 rounded-full border border-border flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                </div>
                <button data-testid={`drawer-remove-${c.key}`} onClick={() => removeItem(c.key)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          {cart.length > 0 && (
            <div className="border-t border-border pt-4 pb-5 mt-4 shrink-0 bg-background">
              <div className="flex justify-between font-display font-extrabold text-lg mb-4">
                <span>Subtotal</span><span data-testid="cart-total">{fmtINR(subtotal)}</span>
              </div>
              <motion.button whileHover={{ scale: 1.02, y: -1 }} whileTap={{ scale: 0.97 }}
                data-testid="checkout-button" onClick={() => { setCartOpen(false); navigate(storePath(slug, "/checkout")); }}
                className="w-full py-4 rounded-full font-extrabold text-[15px] tracking-wide shadow-xl hover:shadow-2xl transition-shadow"
                style={{ backgroundColor: "var(--sp)", color: "#FFFFFF" }}>
                Proceed to Checkout · {fmtINR(subtotal)} <ArrowRight className="inline h-4 w-4 ml-1" />
              </motion.button>
              <button data-testid="view-cart-page" onClick={() => { setCartOpen(false); navigate(storePath(slug, "/cart")); }}
                className="w-full mt-3 py-1.5 text-[11px] font-extrabold uppercase tracking-widest hover:underline underline-offset-4 transition-opacity hover:opacity-70"
                style={{ color: "var(--sp)" }}>
                View full cart page
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ===== WISHLIST DRAWER ===== */}
      <Sheet open={wishOpen} onOpenChange={setWishOpen}>
        <SheetContent className="w-full sm:max-w-md" style={vars}>
          <SheetHeader><SheetTitle className="font-display">Wishlist ({wish.length})</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-4 overflow-y-auto max-h-[75vh]">
            {wish.length === 0 && <p data-testid="wishlist-empty" className="text-sm text-muted-foreground text-center py-12">Nothing saved yet — tap the heart on any product.</p>}
            {wish.map((w) => (
              <div key={w.id} data-testid={`wishlist-item-${w.id}`} className="flex gap-3 items-center border border-border rounded-lg p-3">
                <div className="h-14 w-14 rounded-md bg-muted overflow-hidden shrink-0">{w.image && <img src={w.image} alt="" className="h-full w-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{w.name}</p>
                  <p className="text-xs text-muted-foreground">{fmtINR(w.price)}</p>
                </div>
                <button data-testid={`wishlist-move-${w.id}`}
                  onClick={() => { const p = products.find((x) => x.id === w.id); if (p) { addToCart(p); toggleWish(p); } else toast.error("Product unavailable"); }}
                  className="px-3.5 py-2 rounded-full text-xs font-bold text-white active:scale-95 transition-transform"
                  style={{ backgroundColor: "var(--sp, #111)" }}>
                  Move to Cart
                </button>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
